/**
 * AdminReconciliation
 *
 * Runs a Stripe ↔ Firestore reconciliation check and displays the results:
 *   - Aggregate stats (FS vs Stripe totals, diffs)
 *   - Filterable itemized table (per order)
 *   - Run history (last 5 reports)
 */

import { useState, useEffect, useCallback } from 'react';
import {
  runReconciliationFn,
  getReconciliationReport,
  getLatestReconciliationReports,
  type ReconReport,
  type ReconItem,
} from '../../services/reconciliation';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCents(cents: number | undefined): string {
  if (cents === undefined || cents === null) return '-';
  const abs = Math.abs(cents);
  const str = `$${(abs / 100).toFixed(2)}`;
  return cents < 0 ? `-${str}` : str;
}

function formatDate(iso: string | undefined): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function truncatePi(id: string | null): string {
  if (!id) return '-';
  return id.length > 12 ? `${id.slice(0, 12)}…` : id;
}

const ISSUE_LABELS: Record<string, { label: string; color: string }> = {
  missing_in_stripe:    { label: 'Not in Stripe',      color: 'bg-red-100 text-red-700' },
  missing_in_firestore: { label: 'Not in Firestore',   color: 'bg-purple-100 text-purple-700' },
  amount_mismatch:      { label: 'Amount mismatch',    color: 'bg-orange-100 text-orange-700' },
  status_mismatch:      { label: 'Status mismatch',    color: 'bg-yellow-100 text-yellow-700' },
  refund_mismatch:      { label: 'Refund missing',     color: 'bg-red-100 text-red-700' },
};

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({
  label, value, sub, diff,
}: {
  label: string;
  value: string;
  sub?: string;
  diff?: boolean;
}) {
  const valueColor = diff
    ? value === '$0.00' ? 'text-green-600' : 'text-red-600'
    : 'text-gray-900';
  return (
    <div className="bg-white rounded-xl border border-gray-200 px-5 py-4">
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
      <p className={`text-xl font-bold mt-1 ${valueColor}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function SummaryPill({
  label, value, color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold ${color}`}>
      <span className="text-[15px] font-bold">{value}</span>
      <span>{label}</span>
    </div>
  );
}

function IssueBadge({ issue }: { issue: string }) {
  const meta = ISSUE_LABELS[issue] ?? { label: issue, color: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold mr-1 ${meta.color}`}>
      {meta.label}
    </span>
  );
}

type FilterTab = 'all' | 'mismatches' | 'missing' | 'missing_firestore' | 'free' | 'matched';

// ── Main component ────────────────────────────────────────────────────────────

export default function AdminReconciliation() {
  const [report, setReport] = useState<ReconReport | null>(null);
  const [history, setHistory] = useState<Omit<ReconReport, 'items'>[]>([]);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterTab>('all');
  const [search, setSearch] = useState('');

  const loadHistory = useCallback(async () => {
    try {
      const h = await getLatestReconciliationReports(5);
      setHistory(h);
      // Auto-load the most recent report
      if (h.length > 0 && !report) {
        const latest = await getReconciliationReport(h[0].id);
        if (latest) setReport(latest);
      }
    } catch (err) {
      console.error('[Reconciliation] load history:', err);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleRun = async () => {
    setRunning(true);
    setError(null);
    try {
      const { data } = await runReconciliationFn();
      const newReport = await getReconciliationReport(data.reportId);
      if (newReport) setReport(newReport);
      // Refresh history
      const h = await getLatestReconciliationReports(5);
      setHistory(h);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reconciliation failed');
    } finally {
      setRunning(false);
    }
  };

  const handleLoadReport = async (id: string) => {
    const r = await getReconciliationReport(id);
    if (r) setReport(r);
  };

  // ── Filter items ────────────────────────────────────────────────────────────

  const filteredItems: ReconItem[] = (report?.items ?? []).filter((item) => {
    const q = search.toLowerCase();
    if (q) {
      const haystack = [
        item.paymentIntentId ?? '',
        item.orderId ?? '',
        item.fs?.playerName ?? '',
        item.fs?.coachName ?? '',
        item.fs?.offeringTitle ?? '',
      ].join(' ').toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    switch (filter) {
    case 'mismatches':
      return !item.isMatch &&
        !item.issues.includes('missing_in_stripe') &&
        !item.issues.includes('missing_in_firestore');
    case 'missing':
      return item.issues.includes('missing_in_stripe');
    case 'missing_firestore':
      return item.issues.includes('missing_in_firestore');
    case 'free':
      return item.paymentIntentId === null;
    case 'matched':
      return item.isMatch && item.paymentIntentId !== null;
    default:
      return true;
    }
  });

  const agg = report?.aggregate;
  const sum = report?.summary;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 max-w-[1200px] mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Stripe Reconciliation</h1>
          {report && (
            <p className="text-xs text-gray-400 mt-0.5">
              Last run: {formatDate(report.runAt)}
              {report.status === 'error' && (
                <span className="ml-2 text-red-500">● Error</span>
              )}
            </p>
          )}
        </div>
        <button
          onClick={handleRun}
          disabled={running}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {running ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Running…
            </>
          ) : (
            <>
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
              Run Reconciliation
            </>
          )}
        </button>
      </div>

      {/* Running banner */}
      {running && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-4 text-sm text-blue-700">
          Fetching data from Firestore and Stripe. This may take up to 30 seconds for large datasets…
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-4 text-sm text-red-700">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Report error */}
      {report?.status === 'error' && report.error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-4 text-sm text-red-700">
          <strong>Last run failed:</strong> {report.error}
        </div>
      )}

      {loading && !report && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}

      {report && report.status === 'complete' && agg && sum && (
        <>
          {/* Aggregate stats */}
          <div>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Aggregate</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <StatCard label="FS Captured" value={formatCents(agg.fs.capturedCents)} sub={`${agg.fs.ordersWithPayment} paid orders`} />
              <StatCard label="FS Transferred" value={formatCents(agg.fs.transferredCents)} />
              <StatCard label="FS Refunded" value={formatCents(agg.fs.refundedCents)} />
              <StatCard label="Stripe Charged" value={formatCents(agg.stripe.totalChargedCents)} sub={`${agg.stripe.piCount} PIs`} />
              <StatCard label="Stripe Refunded" value={formatCents(agg.stripe.totalRefundedCents)} />
              <StatCard
                label="Unmatched in Stripe"
                value={formatCents(agg.stripe.unmatchedChargedCents)}
                sub={agg.stripe.unmatchedPiCount > 0 ? `${agg.stripe.unmatchedPiCount} PIs` : 'None'}
                diff
              />
            </div>
          </div>

          {/* Summary pills */}
          <div className="flex flex-wrap gap-2">
            <SummaryPill label="total orders" value={sum.total} color="bg-gray-100 text-gray-700" />
            <SummaryPill label="matched" value={sum.matched} color="bg-green-100 text-green-700" />
            <SummaryPill
              label="mismatches"
              value={sum.mismatches}
              color={sum.mismatches > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}
            />
            <SummaryPill
              label="not in Stripe"
              value={sum.missingInStripe}
              color={sum.missingInStripe > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}
            />
            <SummaryPill
              label="not in Firestore"
              value={sum.missingInFirestore}
              color={sum.missingInFirestore > 0 ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'}
            />
            <SummaryPill label="free/unpaid" value={sum.freeOrders} color="bg-gray-100 text-gray-500" />
          </div>

          {/* Filter bar */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center gap-1 px-4 pt-3 pb-0 border-b border-gray-100">
              {(
                [
                  { key: 'all', label: 'All' },
                  { key: 'mismatches', label: `Mismatches (${sum.mismatches})` },
                  { key: 'missing', label: `Not in Stripe (${sum.missingInStripe})` },
                  { key: 'missing_firestore', label: `Not in Firestore (${sum.missingInFirestore})` },
                  { key: 'matched', label: `Matched (${sum.matched})` },
                  { key: 'free', label: `Free (${sum.freeOrders})` },
                ] as { key: FilterTab; label: string }[]
              ).map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className={`px-3 py-2 text-[12px] font-semibold border-b-2 transition-colors ${
                    filter === key
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {label}
                </button>
              ))}
              <div className="flex-1" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search player, coach, PI ID…"
                className="mb-2 text-[12px] px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-52 input-glow"
              />
            </div>

            {/* Itemized table */}
            {filteredItems.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-gray-400">No items match this filter</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="bg-gray-50 text-[11px] text-gray-500 uppercase tracking-wide">
                      <th className="px-4 py-2.5 text-left font-medium w-36">PI ID</th>
                      <th className="px-4 py-2.5 text-left font-medium">Player → Coach</th>
                      <th className="px-4 py-2.5 text-left font-medium max-w-[140px]">Offering</th>
                      <th className="px-4 py-2.5 text-right font-medium">FS Amount</th>
                      <th className="px-4 py-2.5 text-right font-medium">Stripe Amount</th>
                      <th className="px-4 py-2.5 text-left font-medium">FS Status</th>
                      <th className="px-4 py-2.5 text-left font-medium">Stripe Status</th>
                      <th className="px-4 py-2.5 text-left font-medium">Issues</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredItems.map((item) => (
                      <tr
                        key={item.orderId ?? item.paymentIntentId}
                        className={item.issues.includes('missing_in_firestore') ? 'bg-purple-50' : item.issues.length > 0 ? 'bg-red-50' : 'hover:bg-gray-50'}
                      >
                        {/* PI ID */}
                        <td className="px-4 py-2.5 font-mono text-gray-500" title={item.paymentIntentId ?? ''}>
                          {truncatePi(item.paymentIntentId)}
                        </td>
                        {/* Player → Coach */}
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-gray-800">{item.fs?.playerName || '-'}</div>
                          <div className="text-gray-400">{item.fs?.coachName || '-'}</div>
                        </td>
                        {/* Offering */}
                        <td className="px-4 py-2.5 text-gray-600 max-w-[140px] truncate">
                          {item.fs?.offeringTitle || '-'}
                        </td>
                        {/* FS Amount */}
                        <td className="px-4 py-2.5 text-right font-mono text-gray-800">
                          {item.fs ? (
                            <>
                              {formatCents(item.fs.totalCents)}
                              {item.fs.totalLessons > 1 && (
                                <div className="text-gray-400 text-[10px]">
                                  {formatCents(item.fs.priceCents)} × {item.fs.totalLessons}
                                </div>
                              )}
                            </>
                          ) : '-'}
                        </td>
                        {/* Stripe Amount */}
                        <td className="px-4 py-2.5 text-right font-mono text-gray-800">
                          {item.stripe ? (
                            <>
                              {formatCents(item.stripe.amountCents)}
                              {item.stripe.refundedCents > 0 && (
                                <div className="text-red-400 text-[10px]">
                                  −{formatCents(item.stripe.refundedCents)} refunded
                                </div>
                              )}
                            </>
                          ) : '-'}
                        </td>
                        {/* FS Status */}
                        <td className="px-4 py-2.5">
                          {item.fs ? (
                            <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-mono text-[10px]">
                              {item.fs.paymentStatus}
                            </span>
                          ) : '-'}
                        </td>
                        {/* Stripe Status */}
                        <td className="px-4 py-2.5">
                          {item.stripe ? (
                            <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-mono text-[10px]">
                              {item.stripe.status}
                            </span>
                          ) : '-'}
                        </td>
                        {/* Issues */}
                        <td className="px-4 py-2.5">
                          {item.issues.length === 0 ? (
                            <span className="text-green-600 text-[11px] font-semibold">✓ OK</span>
                          ) : (
                            item.issues.map((issue) => (
                              <IssueBadge key={issue} issue={issue} />
                            ))
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Run history */}
      {history.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-700">Run History</h2>
          </div>
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-gray-50 text-[11px] text-gray-500 uppercase tracking-wide">
                <th className="px-5 py-2.5 text-left font-medium">Run At</th>
                <th className="px-4 py-2.5 text-left font-medium">Status</th>
                <th className="px-4 py-2.5 text-right font-medium">Total</th>
                <th className="px-4 py-2.5 text-right font-medium">Matched</th>
                <th className="px-4 py-2.5 text-right font-medium">Mismatches</th>
                <th className="px-4 py-2.5 text-right font-medium">Missing</th>
                <th className="px-4 py-2.5 text-right font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {history.map((h) => (
                <tr key={h.id} className={`hover:bg-gray-50 ${report?.id === h.id ? 'bg-blue-50' : ''}`}>
                  <td className="px-5 py-2.5 text-gray-700">{formatDate(h.runAt)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                      h.status === 'complete' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {h.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-600">{h.summary?.total ?? '-'}</td>
                  <td className="px-4 py-2.5 text-right text-green-600 font-medium">{h.summary?.matched ?? '-'}</td>
                  <td className={`px-4 py-2.5 text-right font-medium ${(h.summary?.mismatches ?? 0) > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                    {h.summary?.mismatches ?? '-'}
                  </td>
                  <td className={`px-4 py-2.5 text-right font-medium ${(h.summary?.missingInStripe ?? 0) > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                    {h.summary?.missingInStripe ?? '-'}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {report?.id !== h.id && (
                      <button
                        onClick={() => handleLoadReport(h.id)}
                        className="text-blue-600 hover:underline text-[11px] font-medium"
                      >
                        Load
                      </button>
                    )}
                    {report?.id === h.id && (
                      <span className="text-[11px] text-blue-600 font-semibold">Viewing</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}
